import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssetType, IProjectAsset } from '@packages/global';
import { episodeApi } from '../../services/user';
import { useGlobalLoading } from '../../stores/globalLoadingStore';
import { useToast } from '../../stores/toastStore';
import { useDialog } from '../useDialog';
import { useScriptErrorHandler } from '../useScriptErrorHandler';
import type { GenerateStoryboardConfig, MissingReferences } from '../../components/ScriptGeneration/common';

interface ScriptData {
  id: string;
  shots?: any[];
  characters?: any[];
  props?: any[];
  scenes?: any[];
}

interface UseStoryboardActionsOptions {
  script: ScriptData | null;
  episodeId: string | undefined;
  isRegenerateMode: boolean;
  setIsRegenerateMode: (v: boolean) => void;
  setIsProcessing: (v: boolean) => void;
  setIsFailed: (v: boolean) => void;
  setErrorMessage: (v: string | undefined) => void;
  setShowStoryboardModal: (v: boolean) => void;
  setShowMissingRefsModal: (v: boolean) => void;
  setMissingRefs: (refs: MissingReferences) => void;
  setPendingRegenerateMode: (v: boolean) => void;
  pendingRegenerateMode: boolean;
  /** 剧集主体列表（以 ProjectAsset 为主数据源） */
  episodeAssets?: IProjectAsset[];
}

const SPECIAL_VALUES = new Set(['无', '旁白', '', 'None', 'Narrator', 'none', 'narrator', 'N/A', 'n/a']);

export function useStoryboardActions({
  script, episodeId,
  isRegenerateMode, setIsRegenerateMode,
  setIsProcessing, setIsFailed, setErrorMessage,
  setShowStoryboardModal, setShowMissingRefsModal,
  setMissingRefs, setPendingRegenerateMode, pendingRegenerateMode,
  episodeAssets,
}: UseStoryboardActionsOptions) {
  const navigate = useNavigate();
  const dialog = useDialog();
  const toast = useToast();
  const { showLoading, hideLoading } = useGlobalLoading();
  const { executeWithRetry } = useScriptErrorHandler();

  const checkMissingReferences = useCallback((): MissingReferences => {
    const shots = script?.shots || [];
    // 主体数据以 ProjectAsset 为主数据源；降级使用 script JSON（用于旧数据兼容）
    const assets = episodeAssets || [];
    const characterNames = new Set<string>(
      assets.length > 0
        ? assets.filter(a => a.assetType === AssetType.CHARACTER).map(a => a.name)
        : (script?.characters || []).map((c: any) => c.name)
    );
    const propNames = new Set<string>(
      assets.length > 0
        ? assets.filter(a => a.assetType === AssetType.PROP).map(a => a.name)
        : (script?.props || []).map((p: any) => p.name)
    );
    const sceneNames = new Set<string>(
      assets.length > 0
        ? assets.filter(a => a.assetType === AssetType.SCENE).map(a => a.name)
        : (script?.scenes || []).map((s: any) => s.name)
    );

    const missingCharacters = new Set<string>();
    const missingProps = new Set<string>();
    const missingScenes = new Set<string>();
    const missingVoiceRoles = new Set<string>();

    for (const shot of shots) {
      if (shot.sceneName && !SPECIAL_VALUES.has(shot.sceneName) && !sceneNames.has(shot.sceneName))
        missingScenes.add(shot.sceneName);
      if (shot.voiceRole && !SPECIAL_VALUES.has(shot.voiceRole) && !characterNames.has(shot.voiceRole))
        missingVoiceRoles.add(shot.voiceRole);
      for (const subject of shot.subjects || [])
        if (!SPECIAL_VALUES.has(subject) && !characterNames.has(subject)) missingCharacters.add(subject);
      for (const prop of shot.props || [])
        if (!SPECIAL_VALUES.has(prop) && !propNames.has(prop)) missingProps.add(prop);
    }

    return {
      characters: Array.from(missingCharacters),
      props: Array.from(missingProps),
      scenes: Array.from(missingScenes),
      voiceRoles: Array.from(missingVoiceRoles),
    };
  }, [script?.shots, script?.characters, script?.props, script?.scenes, episodeAssets]);

  const hasMissingReferences = (refs: MissingReferences) =>
    refs.characters.length > 0 || refs.props.length > 0 || refs.scenes.length > 0 || refs.voiceRoles.length > 0;

  const openStoryboardModal = useCallback((regenerate: boolean) => {
    if (!script?.id || !episodeId) return;
    const refs = checkMissingReferences();
    if (hasMissingReferences(refs)) {
      setMissingRefs(refs);
      setPendingRegenerateMode(regenerate);
      setShowMissingRefsModal(true);
      return;
    }
    setIsRegenerateMode(regenerate);
    setShowStoryboardModal(true);
  }, [script?.id, episodeId, checkMissingReferences]);

  const handleGenerateStoryboard = useCallback(() => openStoryboardModal(false), [openStoryboardModal]);
  const handleRegenerateStoryboard = useCallback(() => openStoryboardModal(true), [openStoryboardModal]);

  const handleMissingRefsConfirm = useCallback(() => {
    setShowMissingRefsModal(false);
    setIsRegenerateMode(pendingRegenerateMode);
    setShowStoryboardModal(true);
  }, [pendingRegenerateMode]);

  const handleStoryboardConfirm = useCallback(async (config: GenerateStoryboardConfig) => {
    if (!script?.id || !episodeId) return;
    setShowStoryboardModal(false);

    try {
      setIsProcessing(true);
      setIsFailed(false);
      setErrorMessage(undefined);
      showLoading(isRegenerateMode ? '正在重新生成分镜，请稍候...' : '正在生成分镜，请稍候...');

      await executeWithRetry(async () => {
        if (isRegenerateMode) {
          const { newEpisodeId } = await episodeApi.regenerateStoryboard(episodeId, {
            scriptId: script.id, ...config, aspectRatio: config.aspectRatio as any
          });
          await episodeApi.batchGenerateResources(newEpisodeId, {
            text2imageModelId: config.text2imageModelId,
            imageEditModelId: config.imageEditModelId,
            text2speechModelId: config.text2speechModelId,
            videoAspectRatio: config.aspectRatio as any,
            generateImages: config.generateImages,
            generateEndFrame: config.generateEndFrame
          });
          navigate(`/episode/${newEpisodeId}/batch-resource-loading`);
        } else {
          await episodeApi.generateVideoClips(episodeId, {
            scriptId: script.id, ...config, aspectRatio: config.aspectRatio as any
          });
          await episodeApi.batchGenerateResources(episodeId, {
            text2imageModelId: config.text2imageModelId,
            imageEditModelId: config.imageEditModelId,
            text2speechModelId: config.text2speechModelId,
            videoAspectRatio: config.aspectRatio as any,
            generateImages: config.generateImages,
            generateEndFrame: config.generateEndFrame
          });
          navigate(`/episode/${episodeId}/batch-resource-loading`);
        }
      }, { onRetry: (count) => toast.info(`正在重试 (${count}/3)...`) });
    } catch (err: any) {
      setIsFailed(true);
      const errMsg = err.response?.data?.message || err.message || '生成分镜失败，请重试';
      setErrorMessage(errMsg);
      dialog.alert(errMsg, '生成失败', 'danger');
    } finally {
      setIsProcessing(false);
      hideLoading();
    }
  }, [script?.id, episodeId, isRegenerateMode]);

  return {
    handleGenerateStoryboard,
    handleRegenerateStoryboard,
    handleMissingRefsConfirm,
    handleStoryboardConfirm,
  };
}
